import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Save, Percent, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export default function CommissionPanel() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ["commission-settings"], queryFn: () => api.get("/admin/commission-settings").then((r) => r.data) });
  const { data: overrides = [] } = useQuery({ queryKey: ["commission-overrides"], queryFn: () => api.get("/admin/commission-overrides").then((r) => r.data) });
  const { data: enhancers = [] } = useQuery({ queryKey: ["enhancers-lite"], queryFn: () => api.get("/admin/enhancers-lite").then((r) => r.data) });

  const [enabled, setEnabled] = useState(false);
  const [tiers, setTiers] = useState([]);
  useEffect(() => { if (settings) { setEnabled(!!settings.tiered_enabled); setTiers(settings.tiers || []); } }, [settings]);

  const [ov, setOv] = useState({ entity_type: "enhancer", entity_id: "", commission_pct: 15 });

  const saveSettings = useMutation({
    mutationFn: () => api.put("/admin/commission-settings", { tiered_enabled: enabled, volume_basis: "monthly", tiers }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["commission-settings"] }); toast.success("Commission settings saved"); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const saveOverride = useMutation({
    mutationFn: () => {
      const name = enhancers.find((e) => e.id === ov.entity_id)?.name || "";
      return api.put("/admin/commission-overrides", { ...ov, entity_name: name });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["commission-overrides"] }); toast.success("Override saved"); setOv({ entity_type: "enhancer", entity_id: "", commission_pct: 15 }); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const delOverride = useMutation({
    mutationFn: (id) => api.delete(`/admin/commission-overrides/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["commission-overrides"] }); toast.success("Removed"); },
  });

  const setTier = (i, k, v) => setTiers((t) => t.map((x, j) => (j === i ? { ...x, [k]: Number(v) || 0 } : x)));
  const addTier = () => setTiers((t) => [...t, { min_monthly: 0, max_monthly: 0, commission_pct: 15 }]);
  const rmTier = (i) => setTiers((t) => t.filter((_, j) => j !== i));

  return (
    <div data-testid="admin-commission-panel">
      <h2 className="font-serif text-2xl text-[#1A242B] mb-1">Commissions & finder's fees</h2>
      <p className="text-sm text-[#4A5D6B] mb-6">The commission % is the ecosystem's share of each transaction. Enable tiers so the more someone earns in a month, the lower your fee.</p>

      <div className="bg-white rounded-2xl border border-[#144F75]/12 shadow-sm p-6 mb-8">
        <label className="flex items-center gap-3 mb-4">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="commission-tiered-toggle" />
          <span className="font-medium text-[#1A242B] flex items-center gap-2"><TrendingDown size={16} className="text-[#144F75]" /> Tiered (monthly-volume) commissions</span>
        </label>
        {enabled && (
          <div className="space-y-2" data-testid="commission-tiers">
            <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 text-xs uppercase tracking-wide text-[#8FA89B] font-semibold px-1">
              <span>Monthly from ($)</span><span>Up to ($, 0=∞)</span><span>Commission %</span><span></span>
            </div>
            {tiers.map((t, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
                <input type="number" value={t.min_monthly} onChange={(e) => setTier(i, "min_monthly", e.target.value)} className="eyw-input w-full text-sm" />
                <input type="number" value={t.max_monthly} onChange={(e) => setTier(i, "max_monthly", e.target.value)} className="eyw-input w-full text-sm" />
                <input type="number" value={t.commission_pct} onChange={(e) => setTier(i, "commission_pct", e.target.value)} data-testid={`tier-pct-${i}`} className="eyw-input w-full text-sm" />
                <button onClick={() => rmTier(i)} className="text-[#B45151]"><Trash2 size={15} /></button>
              </div>
            ))}
            <button onClick={addTier} className="text-sm text-[#144F75] hover:underline inline-flex items-center gap-1 mt-1"><Plus size={14} /> Add tier</button>
          </div>
        )}
        <button onClick={() => saveSettings.mutate()} data-testid="commission-save-settings" className="eyw-btn !py-2 text-sm mt-5"><Save size={15} /> Save settings</button>
      </div>

      <div className="bg-white rounded-2xl border border-[#144F75]/12 shadow-sm p-6">
        <h3 className="font-medium text-[#1A242B] mb-1 flex items-center gap-2"><Percent size={16} className="text-[#144F75]" /> Per-entity overrides</h3>
        <p className="text-sm text-[#4A5D6B] mb-4">Set a fixed commission for a specific enhancer, location, or vendor (overrides tiers).</p>
        <div className="grid sm:grid-cols-[1fr_2fr_1fr_auto] gap-2 items-center mb-5">
          <select value={ov.entity_type} onChange={(e) => setOv({ ...ov, entity_type: e.target.value, entity_id: "" })} className="eyw-input w-full text-sm">
            <option value="enhancer">Enhancer</option><option value="location">Location</option><option value="vendor">Vendor</option>
          </select>
          <select value={ov.entity_id} onChange={(e) => setOv({ ...ov, entity_id: e.target.value })} data-testid="override-entity" className="eyw-input w-full text-sm">
            <option value="">Select {ov.entity_type}…</option>
            {ov.entity_type === "enhancer" && enhancers.map((e) => <option key={e.id} value={e.id}>{e.name || e.email}</option>)}
          </select>
          <input type="number" placeholder="%" value={ov.commission_pct} onChange={(e) => setOv({ ...ov, commission_pct: Number(e.target.value) || 0 })} data-testid="override-pct" className="eyw-input w-full text-sm" />
          <button onClick={() => ov.entity_id ? saveOverride.mutate() : toast.error("Pick an entity")} data-testid="override-save" className="eyw-btn !py-2 text-sm">Add</button>
        </div>
        <div className="space-y-2">
          {overrides.map((o) => (
            <div key={o.id} data-testid={`override-${o.id}`} className="flex items-center justify-between bg-[#F9F7F3] rounded-lg px-3 py-2 text-sm">
              <span className="capitalize">{o.entity_type}: <strong>{o.entity_name || o.entity_id}</strong> — {o.commission_pct}%</span>
              <button onClick={() => delOverride.mutate(o.id)} className="text-[#B45151]"><Trash2 size={14} /></button>
            </div>
          ))}
          {overrides.length === 0 && <p className="text-sm text-[#8FA89B]">No overrides — everyone uses tiers or their default rate.</p>}
        </div>
      </div>
    </div>
  );
}
